import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import React from 'react'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  SaasCancelSubscriptionFailureToastMessage,
  SaasCancelSubscriptionSuccessToastMessage,
  SaasEnterpriseRequestFailureToastMessage,
  SaasEnterpriseRequestSuccessToastMessage,
} from 'src/app/Toast/Toast'
import { useBackgroundDispatch } from 'src/hooks'
import {
  ChurnSurveyReasonsResponse,
  ChurnSurveyRequest,
  OrganizationSaasTierResponse,
  OrganizationUsagesResponse,
  SaasTierType,
  SaasUrlResponse,
  SubscriptionResponse,
} from 'src/models/organizations.models'
import {
  getChurnSurveyReasons,
  getOrganizationSaasTier,
  getOrganizationSubscription,
  getOrganizationUsages,
} from 'src/queries/organizations/saas.network'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  RequestConfig,
  useExtendMutation,
  UseExtendMutationVariablesBody,
} from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'
import type { BackendErrorPayload } from 'src/utils/error.utils'

// Reproduce useExtendQuery's error contract: expose the backend payload from the
// axios error's response (undefined when there is no response), not a normalized
// always-truthy object. Consumers gate UI on this exact shape.
const toBackendError = (
  error: unknown,
): BackendErrorPayload<string> | undefined =>
  (error as AxiosError<BackendErrorPayload<string>> | null)?.response?.data

export const saasQueries = {
  tier: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.saasTier(orgId),
      queryFn: () => {
        if (!orgId) {
          throw new Error('saasQueries.tier: orgId is required')
        }
        return getOrganizationSaasTier(orgId)
      },
      enabled: !!orgId,
    }),
  usages: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.organizationUsages(orgId),
      queryFn: () => {
        if (!orgId) {
          throw new Error('saasQueries.usages: orgId is required')
        }
        return getOrganizationUsages(orgId)
      },
      enabled: !!orgId,
    }),
  subscription: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.organizationSubscription(orgId),
      queryFn: () => {
        if (!orgId) {
          throw new Error('saasQueries.subscription: orgId is required')
        }
        return getOrganizationSubscription(orgId)
      },
      enabled: !!orgId,
    }),
  churnSurveyReasons: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.churnSurveyReasons(orgId),
      queryFn: () => {
        if (!orgId) {
          throw new Error('saasQueries.churnSurveyReasons: orgId is required')
        }
        return getChurnSurveyReasons(orgId)
      },
      enabled: !!orgId,
    }),
}

interface OrganizationSaasTierOptions
  extends ExtendBaseQueryOptions<OrganizationSaasTierResponse> {
  orgId: string | undefined
}

export const useQueryOrganizationSaasTier = ({
  orgId,
  enabled = true,
  queryCacheOptions,
  ...rest
}: OrganizationSaasTierOptions) => {
  const query = useQuery({
    ...saasQueries.tier(orgId),
    ...queryCacheOptions,
    ...rest,
    // wait for orgId to be defined before making the request
    enabled: enabled && !!orgId,
  })
  return { ...query, error: toBackendError(query.error) }
}

/**
 * @returns whether an org has access to saas features
 */
export const useHasSaasFeatures = (): boolean => {
  // A user has SaaS features if they are excluded from SaaS billing
  // or if their org is on a paid tier (not Starter)

  const { data: user } = useQueryOwnUser({})

  const { data: organizationTier, isFetched: saasTierIsFetched } =
    useQueryOrganizationSaasTier({ orgId: user?.organizationId })

  const hasSaasFeatures =
    (!organizationTier && saasTierIsFetched) || // excluded from SaaS OR
    (organizationTier !== undefined && organizationTier?.tier !== 'STARTER') // not Starter tier

  return hasSaasFeatures
}

interface QueryOrganizationUsagesOptions
  extends ExtendBaseQueryOptions<
    OrganizationUsagesResponse,
    OrganizationUsagesResponse
  > {
  orgId: string | undefined
}

export const useQueryOrganizationUsages = ({
  orgId,
  enabled = true,
  ...rest
}: QueryOrganizationUsagesOptions) => {
  const query = useQuery({
    ...saasQueries.usages(orgId),
    ...rest,
    enabled: enabled && !!orgId,
  })
  return { ...query, error: toBackendError(query.error) }
}

interface QueryOrganizationSubscriptionOptions
  extends ExtendBaseQueryOptions<SubscriptionResponse, SubscriptionResponse> {
  orgId: string | undefined
}

export const useQueryOrganizationSubscription = ({
  orgId,
  enabled = true,
  ...rest
}: QueryOrganizationSubscriptionOptions) => {
  const query = useQuery({
    ...saasQueries.subscription(orgId),
    ...rest,
    enabled: enabled && !!orgId,
  })
  return { ...query, error: toBackendError(query.error) }
}

interface PostCreateSubscriptionRequest
  extends UseExtendMutationVariablesBody<{
    billingPeriod: string
    plan?: Extract<SaasTierType, 'STARTER' | 'PRO'>
    issuerId?: string
  }> {
  orgId: string
  redirect: boolean
}

export const usePostCreateSubscription = () =>
  useExtendMutation<SaasUrlResponse, PostCreateSubscriptionRequest>(
    MutationMethod.POST,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/createsubscription`,
      requestConfig: RequestConfig.Default,
      onSuccess: ({ url }, { redirect = true }) => {
        if (redirect && url !== null) {
          window.location.href = url
        }
        return url
      },
    },
  )

interface PutManageSubscriptionRequest
  extends UseExtendMutationVariablesBody<{}> {
  orgId: string
  redirect: boolean
}

export const usePutManageSubscription = () =>
  useExtendMutation<SaasUrlResponse, PutManageSubscriptionRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/managesubscription`,
      requestConfig: RequestConfig.Default,
      onSuccess: ({ url }, { redirect = true }) => {
        if (redirect && url !== null) {
          window.location.href = url
        }
        return url
      },
    },
  )

interface PutReactivateSubscriptionRequest
  extends UseExtendMutationVariablesBody<{ billingPeriod: string }> {
  orgId: string
  redirect: boolean
}

export const usePutReactivateSubscription = () =>
  useExtendMutation<SaasUrlResponse, PutReactivateSubscriptionRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/reactivatesubscription`,
      requestConfig: RequestConfig.Default,
      onSuccess: ({ url }, { redirect = true }) => {
        if (redirect && url !== null) {
          window.location.href = url
        }
        return url
      },
    },
  )

interface PutRenewSubscriptionRequest
  extends UseExtendMutationVariablesBody<{}> {
  orgId: string
}

export const usePutRenewSubscriptionRequest = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<boolean, PutRenewSubscriptionRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/uncancelsubscription`,
      requestConfig: RequestConfig.Default,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: orgQueryKeys.organizationSubscription(variables.orgId),
        })
      },
    },
  )
}

interface PostEnterpriseRequest
  extends UseExtendMutationVariablesBody<{
    intention: string
    issuerId?: string
  }> {
  orgId: string
}

export const usePostEnterpriseRequest = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const dispatch = useBackgroundDispatch()
  return useExtendMutation<{ response: string }, PostEnterpriseRequest>(
    MutationMethod.POST,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/enterpriserequest`,
      onSuccess: () => {
        if (!showSuccessToast) return
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <SaasEnterpriseRequestSuccessToastMessage />,
          }),
        )
      },
      onError: () => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <SaasEnterpriseRequestFailureToastMessage />,
            failureColorScheme: true,
            hideImage: true,
          }),
        )
      },
    },
  )
}

interface CancelSubscriptionRequest extends UseExtendMutationVariablesBody {
  orgId: string
  tier?: SaasTierType
}

export const useCancelSubscription = () => {
  const queryClient = useQueryClient()
  const dispatch = useBackgroundDispatch()
  return useExtendMutation<unknown, CancelSubscriptionRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ orgId }) => `/saas/${orgId}/cancelsubscription`,
      onSuccess: (_, { tier, orgId }) => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <SaasCancelSubscriptionSuccessToastMessage tier={tier} />
            ),
          }),
        )
        queryClient.invalidateQueries({
          queryKey: orgQueryKeys.organizationSubscription(orgId),
        })
      },
      onError: () => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <SaasCancelSubscriptionFailureToastMessage />,
            failureColorScheme: true,
            hideImage: true,
          }),
        )
      },
    },
  )
}

interface PostChurnSurveyRequest
  extends UseExtendMutationVariablesBody<ChurnSurveyRequest> {
  orgId: string
}

export const usePostChurnSurvey = () =>
  useExtendMutation<unknown, PostChurnSurveyRequest>(MutationMethod.POST, {
    mutationUrl: ({ orgId }) => `/saas/${orgId}/churnsurvey`,
    onError: () => {
      // Fire-and-forget — fail silently
    },
  })

interface QueryChurnSurveyReasonsOptions
  extends ExtendBaseQueryOptions<ChurnSurveyReasonsResponse> {
  orgId: string | undefined
}

export const useQueryChurnSurveyReasons = ({
  orgId,
  enabled = true,
  ...rest
}: QueryChurnSurveyReasonsOptions) => {
  const query = useQuery({
    ...saasQueries.churnSurveyReasons(orgId),
    ...rest,
    enabled: enabled && !!orgId,
  })
  return { ...query, error: toBackendError(query.error) }
}
